<template>
    <v-card
        id="editor-settings"
        elevation="1"
        :class="cardClass"
        :loading="loading"
        @click.passive="setActive()"
    >
        <expansion-panel-header
            :tooltipText="tooltipText"
            :collapsable="collapsable"
            :collapsed="settingsCollapsed"
            :loading="loading"
            @click="settingsCollapsed = !settingsCollapsed"
        >
            {{ t_settings_of + t_title() }}
            <div class="label ml-2">
                {{t_duration }}: {{ summedDuration() }}
            </div>
        </expansion-panel-header>

        <div v-show="!settingsCollapsed || grid==='3cols'">
            <div ref="scrollBox" class="scrollBox" :style="heightStyle" @scroll="scrolled()">

                <settings-container v-if="sectionAvailable('widget')"
                                    :title="t_widget_settings"
                                    initially-open>
                    <widget-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :element_nr="inner_properties.index"
                        :loading="loading"
                        :widget_settings="widget_settings[inner_properties.id]"
                        :widget_properties="widget_properties[inner_properties.id]"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">
                    </widget-settings>
                </settings-container>

                <div class="noCollapse">
                    <div v-if="readonly && inner_campaign.dynamic === false" class="alert alert-danger">
                        {{ t_settings_readonly }}
                    </div>
                    <div v-if="creator === false" class="alert alert-danger">
                        <div>{{ t_creator_invalid }}</div>
                        <template v-for="element in creator_errors">
                            <template v-if="element.errors.length > 0">
                                <div>{{ element.name }}</div>
                                <ul>
                                    <li v-for="error in element.errors">
                                        {{ error }}
                                    </li>
                                </ul>
                            </template>
                        </template>
                    </div>

                    <common-no-collapse-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        :all_layer_layers="all_layer_layers"
                        :parent_layers="innerLayers"
                        :esign-connection-direct="esignConnectionDirect"
                        @nameUpdated="nameUpdated()">

                        <v-row v-if="propertyAvailable('name')">
                            <v-col cols="12" v-if="propertyAvailable('name')">
                                <v-text-field
                                    v-model="inner_properties.name"
                                    :label="t_name"
                                    :disabled="element_readonly('name')"
                                    @change="nameUpdated() || !is_parent_campaign"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <v-row v-if="propertyAvailable('dynamic')">
                            <v-col cols="12">
                                <Checkbox
                                    class="mr-2"
                                    :binary="true"
                                    :modelValue="inner_properties.dynamic"
                                    :label="t_dynamic"
                                    :disabled="element_readonly('dynamic')"
                                    @click="inner_properties.dynamic = !inner_properties.dynamic"
                                />
                                {{ t_dynamic }}
                            </v-col>
                        </v-row>

                        <v-row v-if="propertyAvailable('tags') && inner_properties.dynamic">
                            <v-col cols="12" id="select-tags">
                                <autocomplete-addable
                                    id="autocomplete-tags"
                                    v-model="tags"
                                    :label="t_tags"
                                    :items="inner_properties.all_tags_dyn"
                                    attach="#select-tags .v-select"
                                    :outlined="outlined" :dense="dense" hide-details="auto"
                                    multiple="multiple"
                                    :disabled="element_locked('tags')"
                                    :createable-values="tagCreationPrivilege">
                                </autocomplete-addable>
                            </v-col>
                        </v-row>

                        <v-row v-if="is_dynamic && propertyAvailable('dynamic')">
                            <v-col cols="12">
                                <div id="checkbox-strict_mode_tags">
                                    <Checkbox
                                        class="mr-2"
                                        :binary="true"
                                        :modelValue="inner_properties.strict_mode_tags"
                                        :label="t_strict_mode_tags"
                                        :disabled="element_readonly('dynamic')"
                                        @click="inner_properties.strict_mode_tags = !inner_properties.strict_mode_tags"
                                    />
                                    {{ t_strict_mode_tags }}
                                </div>
                                <v-tooltip-wrapper activator="#checkbox-strict_mode_tags">
                                    <span>{{ t_strict_mode_description_tags }}</span>
                                </v-tooltip-wrapper>
                            </v-col>
                        </v-row>

                        <v-row v-if="is_dynamic && propertyAvailable('dynamic')">
                            <v-col cols="12">
                                <Checkbox
                                    class="mr-2"
                                    :binary="true"
                                    :modelValue="inner_properties.dynamic_autoremove"
                                    :label="t_dynamic_autoremove"
                                    :disabled="element_readonly('dynamic')"
                                    @click="inner_properties.dynamic_autoremove = !inner_properties.dynamic_autoremove"
                                />
                                {{ t_dynamic_autoremove }}
                            </v-col>
                        </v-row>
                    </common-no-collapse-settings>
                </div>

                <settings-container v-if="sectionAvailable('font')"
                                    :title="t_font">
                    <font-settings
                        v-model="inner_properties"
                        :readonly="readonly"
                        :definition="elementProperties()"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">
                    </font-settings>
                </settings-container>

                <settings-container v-if="sectionAvailable('extended')"
                                    :title="t_extended_properties">
                    <extended-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">
                    </extended-settings>
                </settings-container>

                <settings-container v-if="sectionAvailable('playback')"
                                    :title="t_playback_properties">
                    <playbackSettings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :is_parent="is_parent_campaign"
                        is_publish="0"
                        :playback-privilege="playbackPrivilege"
                        :default_duration="inner_campaign.default_duration"
                        :date-privilege="inner_properties && (
                            (!['picture', 'video', 'pdf', 'ppt', 'template', 'campaign'].includes(inner_properties.type)) ||
                            (inner_properties.type === 'template' && editPrivilegeTemplate) ||
                            (inner_properties.type === 'campaign' && editPrivilegeCampaign) ||
                            (['picture', 'video', 'pdf', 'ppt'].includes(inner_properties.type) && editPrivilegeMediapool))"
                    />
                </settings-container>

                <settings-container v-if="sectionAvailable('user')"
                                    :title="t_user_properties">
                    <v-row v-if="propertyAvailable('locked') && !element_readonly('locked')">
                        <v-col cols="12">
                            <div id="checkbox-locked">
                                <Checkbox
                                    class="mr-2"
                                    :binary="true"
                                    :modelValue="inner_properties.locked"
                                    :label="t_locked"
                                    :disabled="element_readonly('locked')"
                                    @click="inner_properties.locked = !inner_properties.locked"
                                />
                                {{ t_locked }}
                            </div>
                            <v-tooltip-wrapper activator="#checkbox-locked">
                                <span>{{ t_locked_description }}</span>
                            </v-tooltip-wrapper>
                        </v-col>
                    </v-row>
                    <v-row v-if="propertyAvailable('usergroups')">
                        <v-col cols="12" id="select-usergroups">
                            <v-autocomplete
                                v-model="inner_properties.usergroups"
                                :label="t_usergroups"
                                :items="inner_properties.all_usergroups"
                                attach="#select-usergroups .v-select"
                                :outlined="outlined" :dense="dense" hide-details="auto"
                                multiple
                                :disabled="element_locked('usergroups')">
                            </v-autocomplete>
                            <v-tooltip-wrapper activator="#select-usergroups .v-select">
                                <span>{{ t_usergroups_description }}</span>
                            </v-tooltip-wrapper>
                        </v-col>
                        <v-col cols="12">
                            <div id="checkbox-strict_mode_usergroups">
                                <Checkbox
                                    class="mr-2"
                                    :binary="true"
                                    :modelValue="inner_properties.strict_mode_usergroups"
                                    :label="t_strict_mode_usergroups"
                                    :disabled="element_locked('usergroups')"
                                    @click="inner_properties.strict_mode_usergroups = !inner_properties.strict_mode_usergroups"
                                />
                                {{ t_strict_mode_usergroups }}
                            </div>
                            <v-tooltip-wrapper activator="#checkbox-strict_mode_usergroups">
                                <span>{{ t_strict_mode_usergroups_description }}</span>
                            </v-tooltip-wrapper>
                        </v-col>
                    </v-row>
                    <v-row v-if="propertyAvailable('layers')">
                        <v-col cols="12">
                            <Checkbox
                                class="mr-2"
                                :binary="true"
                                :modelValue="ignore_layer_size"
                                :label="t_ignore_layer_resolution"
                                :disabled="element_locked('layers')"
                                @click="ignore_layer_size = !ignore_layer_size"
                            />
                            {{ t_ignore_layer_resolution }}
                        </v-col>
                        <v-col cols="12" id="select-layers">
                            <v-autocomplete
                                v-model="inner_properties.layers"
                                :label="t_layers"
                                :items="select_layer_items()"
                                attach="#select-layers .v-select"
                                :outlined="outlined" :dense="dense" hide-details="auto"
                                multiple
                                :disabled="element_locked('usergroups')">
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                    <v-row v-if="propertyAvailable('tags') && inner_properties.dynamic === false">
                        <v-col cols="12" id="select-tags">
                            <autocomplete-addable
                                id="autocomplete-tags"
                                v-model="inner_properties.tags"
                                :label="t_tags"
                                :items="inner_properties.all_tags"
                                attach="#select-tags .v-select"
                                :outlined="outlined" :dense="dense" hide-details="auto"
                                multiple="multiple"
                                :disabled="element_locked('tags')"
                                :createable-values="tagCreationPrivilege">
                            </autocomplete-addable>
                        </v-col>
                    </v-row>
                    <v-row v-if="propertyAvailable('width') || propertyAvailable('height') || propertyAvailable('ratio')">
                        <v-col cols="4" v-if="propertyAvailable('width')">
                            <number-field
                                v-model="width"
                                :label="t_width"
                                :disabled="element_locked('width')"
                                :limits="getFieldEvaluatedLimits('width')"
                                :outlined="outlined" :dense="dense" hide-details="auto">
                            </number-field>
                        </v-col>
                        <v-col cols="4" v-if="propertyAvailable('height')">
                            <number-field
                                v-model="height"
                                :label="t_height"
                                :disabled="element_locked('height')"
                                :limits="getFieldEvaluatedLimits('height')"
                                :outlined="outlined" :dense="dense" hide-details="auto">
                            </number-field>
                        </v-col>
                        <v-col cols="4"  v-if="propertyAvailable('ratio')">
                            <v-text-field
                                :value="ratio"
                                :label="t_format"
                                :readonly="true"
                                :outlined="outlined" :dense="dense" hide-details="auto">
                            </v-text-field>
                        </v-col>
                    </v-row>
                </settings-container>
            </div>
        </div>
    </v-card>
</template>

<script>
    import utility from '../../../utility';
    import Autocomplete from "vue2-autocomplete-js";

    import commonSettingsMixin from "../settings/commonSettingsMixin";
    import commonNoCollapseSettings from "../settings/commonNoCollapseSettings";
    import fontSettings from "../settings/fontSettings";
    import extendedSettings from "../settings/extendedSettings";
    import playbackSettings from "../settings/playbackSettings";
    import widgetSettings from "../settings/widgetSettings";
    import elementTypes from "../elements/types";
    import VTooltipWrapper from '../../common/v-tooltip-wrapper.vue'
    import PublishTooltipButton from "../common/publish-tooltip-button.vue";
    import SettingsContainer from "../publish/settings-container.vue";
    import ExpansionPanelHeader from "../common/expansion-panel-header.vue";
    import {TIMEOUT_DURATION} from "../common/HeightCalculator";
    import Checkbox
        from "../common/PrimeVue/Checkbox.vue";

    export default {
        components: {
            Checkbox,
            ExpansionPanelHeader,
            SettingsContainer,
            PublishTooltipButton,
            VTooltipWrapper,
            Autocomplete,
            commonNoCollapseSettings,
            playbackSettings,
            fontSettings,
            extendedSettings,
            widgetSettings,
        },
        mixins: [
            commonSettingsMixin
        ],
        props: [
            'data',
            'value',
            'creator',
            'grid',
            'mode',
            'startCollapsed',
            'startActive',
            'parent_layer_limitation',
            'parent_usergroup_limitation',
            'esignConnectionDirect',
            'playbackPrivilege',
            'editPrivilege',
            'editPrivilegeMediapool',
            'editPrivilegeTemplate',
            'editPrivilegeCampaign',
            'tagCreationPrivilege'
        ],
        data: function () {
            return {
                t_settings_of: Lang.langGet('Settings of :name', {name: ''}),
                t_strict_mode_usergroups: Lang.langGet("Strict Mode"),
                t_strict_mode_usergroups_description: Lang.langGet("In \"Strict Mode\" users must have ALL selected usergroups."),
                t_strict_mode_tags: Lang.langGet("Strict Mode for Tags"),
                t_strict_mode_tags_description: Lang.langGet("In \"Strict Mode\" elements must have ALL selected tags."),
                t_dynamic: Lang.langGet("Dynamic Campaign"),
                t_dynamic_autoremove: Lang.langGet("If an element that is used in this campaign loses a tag which is defined above, it will be also removed from this campaign."),
                t_m: Lang.langGet("m"),
                t_s: Lang.langGet("s"),
                t_duration: Lang.langGet("Duration"),
                t_ignore_layer_resolution: Lang.langGet("Ignore resolution"),
                t_creator_invalid: Lang.langGet("Campaign cannot be saved:"),
                ignore_layer_size: false,
                inner_campaign: null,
                inner_properties: {
                    id: null,
                    name: '',
                    usergroups: [],
                    layers: [],
                    tags: [],
                    parameters: {
                        resolution: '1x1',
                        format: '',
                        'valid-from': '',
                        'valid-to': ''
                    },
                    all_usergroups: [],
                    all_layers: [],
                    all_layer_layers: [],
                    all_tags: [],
                    strict_mode_usergroups: false,
                    strict_mode_tags: false,
                    locked: false,
                    readonly: false,
                    dynamic: false,
                    dynamic_autoremove: false,
                    smart_loop: 0,
                    synchronize_playback: '0',
                },
                all_layer_layers: [],
                all_usergroups: [],
                transitions: {},
                saving: false,
                active_element: -1,

                parentLayerLimitation: [],
                parentUsergroupLimitation: [],

                activeChanged: false,
                unsavedChanges: false,
                prev_inner_properties: null,
                opened_panels: [],

                basicPrivilege: true,
                advancedPrivilege: true,

                heightStyle: undefined
            }
        },
        computed: {
            tags: {
                get() {
                    return this.inner_properties.tags.map(tag => +(tag));
                },
                set(tags) {
                    this.inner_properties.tags = tags;
                }
            },
            cardClass() {
                return 'float-e-margins no-margin publish campaign-editor fixed-height';
            },
            dynamic: function () {
                return this.inner_campaign ? this.inner_campaign.dynamic : null;
            },
            default_duration: function () {
                return this.inner_campaign ? parseInt(this.inner_campaign.default_duration??0, 10) : Laravel.default_duration;
            },
            is_dynamic: function () {
                return this.inner_properties.dynamic === true;
            },
            is_parent_campaign: function () {
                return this.active_element === -1;
            },
            valid_from: {
                get: function () {
                    if (this.is_parent_campaign) {
                        return this.inner_properties.parameters['valid-from'];
                    } else {
                        if (this.inner_properties.valid_from != null) {
                            return this.inner_properties.valid_from;
                        } else {
                            return this.inner_properties.validity.from;
                        }
                    }
                },
                set: function (newValue) {
                    if (this.is_parent_campaign) {
                        this.inner_properties.parameters['valid-from'] = newValue;
                    } else {
                        this.inner_properties.valid_from = newValue;
                        let validity = utility.checkValidity(newValue, this.inner_properties.validity.to);
                        this.inner_properties.validity.status = validity.status;
                        this.inner_properties.validity.text = validity.text;
                    }
                }
            },
            valid_to: {
                get: function () {
                    if (this.is_parent_campaign) {
                        return this.inner_properties.parameters['valid-to'];
                    } else {
                        if (this.inner_properties.valid_to != null) {
                            return this.inner_properties.valid_to;
                        } else {
                            return this.inner_properties.validity.to;
                        }
                    }
                },
                set: function (newValue) {
                    if (this.is_parent_campaign) {
                        this.inner_properties.parameters['valid-to'] = newValue;
                    } else {
                        this.inner_properties.valid_to = newValue;
                        let validity = utility.checkValidity(newValue, this.inner_properties.validity.to);
                        this.inner_properties.validity.status = validity.status;
                        this.inner_properties.validity.text = validity.text;
                    }
                }
            },
            innerLayers: function() {
                return this.inner_campaign ? this.inner_campaign.layers : [];
            },
            innerUsergroups: function() {
                return this.inner_campaign ? this.inner_campaign.usergroups : [];
            },
            innerUsergroupsStrict: function() {
                return this.inner_campaign ? this.inner_campaign.strict_mode_usergroups : [];
            },
            readonly: function () {
                return this.inner_properties.readonly;
            },
            width: {
                get: function () {
                    if(this.inner_campaign) {
                        var resolution = this.inner_campaign.parameters ? this.inner_campaign.parameters.resolution.split("x") : [0,0];
                        return resolution[0];
                    }
                    return 0;
                },
                set: function (val) {
                    if(this.inner_campaign) {
                        var resolution = this.inner_campaign.parameters.resolution.split("x");
                        let oldVal = resolution[0];
                        var clamped = this.clampToLimits(val, oldVal, this.getFieldLimits('width', 'base'));
                        Vue.set(this.inner_campaign, "width", 0);
                        Vue.set(this.inner_campaign, "width", clamped);
                        this.inner_campaign.parameters.resolution = clamped + 'x' + resolution[1];
                    }
                }
            },
            height: {
                get: function () {
                    if(this.inner_campaign) {
                        var resolution = this.inner_campaign.parameters ? this.inner_campaign.parameters.resolution.split("x") : [0,0];
                        return resolution[1];
                    }
                    return 0;
                },
                set: function (val) {
                    if(this.inner_campaign) {
                        var resolution = this.inner_campaign.parameters.resolution.split("x");
                        let oldVal = resolution[1];
                        var clamped = this.clampToLimits(val, oldVal, this.getFieldLimits('height', 'base'));
                        Vue.set(this.inner_campaign, "height", 0);
                        Vue.set(this.inner_campaign, "height", clamped);
                        this.inner_campaign.parameters.resolution = resolution[0] + 'x' + clamped;
                    }
                }
            },
            collapsable() {
                if (!this.dragdropExists) return false;
                return this.grid === '2cols';
            },
            tooltipText() {
                return `${this.settingsCollapsed ? this.t_expand : this.t_collapse} ${this.t_settings_of} ${this.t_title()}`
            }
        },
        watch: {
            mode() {
                this.timeoutHeightRecalculation();
            },
            grid(newVal) {
                if (newVal === '2cols') this.settingsCollapsed = false;

                this.timeoutHeightRecalculation();
            },
            settingsCollapsed() {
                this.timeoutHeightRecalculation();
                EaseEventBus.fire('WORKAROUND_recalculate_dragdrop_height');
            },
            ratio: function (val, oldVal) {
                this.inner_properties.parameters.format = val;
            },
            dynamic: function (val, oldVal) {
                EaseEventBus.fire('changeDynamic', val);
                this.dragdropExists = !val;
                this.timeoutHeightRecalculation();
            },
            value: function () {
                this.active_element = this.value;

                this.updateInnerProperties();
                this.opened_panels = [];

                this.validate(this.active_element);
            },
            inner_properties: {
                handler: function () {
                    this.autoUpdateTextAlignment();

                    if(!this.unsavedChanges && this.activeChanged === false && this.innerPropertiesChanged()) {
                        EaseEventBus.fire("unsavedChanges", true);
                    }

                    //if prev_inner_properties.index === -1 we are comparing to the campaign settings and not the settings of an element
                    // => campaign settings for transition and length maye changed
                    if (this.prev_inner_properties !== null && this.is_parent_campaign
                        && this.prev_inner_properties.index === -1) {

                        if (this.inner_properties.transition !== this.prev_inner_properties.transition) {
                            this.updateTransitionForElements();
                            this.prev_inner_properties.transition = this.inner_properties.transition;
                        }
                        if (this.inner_properties.transition_length !== this.prev_inner_properties.transition_length) {
                            this.updateTransitionLengthForElements();
                            this.prev_inner_properties.transition_length = this.inner_properties.transition_length;
                        }
                    }
                },
                deep: true
            },
            data: {
                handler: function () {
                    // prevent endless-loop
                    if (JSON.stringify(this.inner_campaign) !== JSON.stringify(this.data)) {

                        this.inner_campaign = _.extend({}, this.inner_campaign, this.data);

                        this.all_layer_layers = this.inner_campaign.all_layer_layers;
                        this.all_usergroups = this.inner_campaign.all_usergroups.map((item) => { item.value = item.value.toString(); return item });

                        if (this.inner_campaign.parameters && this.inner_campaign.parameters.resolution) {
                            let resolution = this.inner_campaign.parameters.resolution.split('x');
                            this.width = resolution[0];
                            this.height = resolution[1];
                        }

                        this.inner_campaign.all_tags = _.orderBy(this.data.all_tags, 'text').map((item) => { item.value = item.value.toString(); return item });

                        this.updateInnerProperties();

                        this.validateAll();
                    }
                },
                deep: true
            },
            inner_campaign: {
                handler: function () {
                    this.validate(this.active_element);

                    EaseEventBus.fire('receivedChanges');
                    EaseEventBus.fire('settingsCampaignChange', this.inner_campaign);
                },
                deep: true
            },
            innerLayers: {
                handler: function () {
                    EaseEventBus.fire("layersSelectionChanged", this.innerLayers ? this.innerLayers : []);
                },
                deep: true
            },
            innerUsergroups: {
                handler: function () {
                    EaseEventBus.fire("usergroupsSelectionChanged", this.innerUsergroups ? this.innerUsergroups : []);
                },
                deep: true
            },
            innerUsergroupsStrict: function (val) {
                EaseEventBus.fire("usergroupsStrictChanged", val);
            },
        },
        methods: {
            timeoutHeightRecalculation(forceRecalculation) {
                setTimeout(() => {
                    this.heightStyle = this.settingsStyle(forceRecalculation);
                }, TIMEOUT_DURATION);
            },
            updateValidityWorkaround() {
                this.data.elements.forEach(element => {
                    if (!element.valid_from && element.valid_from_origin) {
                        element.valid_from = element.valid_from_origin;
                    }
                    if (!element.valid_to && element.valid_to_origin) {
                        element.valid_to = element.valid_to_origin;
                    }
                });
            },
            autoUpdateTextAlignment: function () {
                if(this.old_text_direction !== null) {
                    if (this.old_text_direction !== this.inner_properties.text_direction) {
                        if (this.inner_properties.text_direction === 'rtl' && this.inner_properties.text_align === 'left') {
                            this.inner_properties.text_align = 'right';
                        }

                        if (this.inner_properties.text_direction === 'ltr' && this.inner_properties.text_align === 'right') {
                            this.inner_properties.text_align = 'left';
                        }
                    }
                }
                this.old_text_direction = this.inner_properties.text_direction;
            },
            innerPropertiesChanged: function () {
                let changes = false;
                if(this.prev_inner_properties !== null && this.prev_inner_properties.index === this.inner_properties.index) {
                    let type = this.inner_properties.type;
                    for (const [property, def] of Object.entries(this.elementProperties())) {
                        let value = "";
                        let prev_value = "";
                        if (def.computed) {
                            value = _.get(this.inner_properties, def.computed);
                            prev_value = _.get(this.prev_inner_properties, def.computed);
                        } else {
                            value = this.inner_properties[property];
                            prev_value = this.prev_inner_properties[property];
                        }
                        //for numerical stability
                        if(!isNaN(value) && value !== '') {
                            value = Math.round(value * 100000) / 100000;
                        }
                        if(!isNaN(prev_value) && value !== '') {
                            prev_value = Math.round(prev_value * 100000) / 100000;
                        }

                        let value_string = '';
                        let prev_value_string = '';
                        if (typeof value === "object") {
                            value_string = JSON.stringify(value);
                        } else {
                            value_string = String(value);
                        }
                        if (typeof prev_value === "object") {
                            prev_value_string = JSON.stringify(prev_value);
                        } else {
                            prev_value_string = String(prev_value);
                        }

                        if(value_string !== prev_value_string) {
                            if(prev_value != null || value !== this.defaultValue(type, property)) {
                                changes = true;
                                break;
                            }
                        }
                    }
                }
                if(!changes) this.prev_inner_properties = JSON.parse(JSON.stringify(this.inner_properties));
                return changes;
            },
            t_title: function (id=null) {
                let element = null;
                if(id === null) {
                    element = this.inner_properties;
                }
                else {
                    if(id === -1) {
                        element = this.inner_campaign;
                    } else {
                        if(this.inner_campaign && this.inner_campaign.elements) {
                            element = this.inner_campaign.elements[id];
                        }
                    }
                }
                let title = '';
                if(element) {
                    let name = element.name;
                    if(name !== '') {
                        name = ' - ' + name;
                    }
                    if(_.size(element.type) === 0) {
                        if(element.dynamic) {
                            title = Lang.langGet("Dynamic Campaign");
                        } else {
                            title = Lang.langChoice("Campaign|Campaigns", 1);
                        }
                    } else {
                        let type = "";
                        if(element.type === 'campaign' && element.dynamic) {
                            type = Lang.langGet("Dynamic Campaign");
                        } else {
                            type = Lang.langGet(elementTypes.titles[element.type]);
                        }
                        if(element.name) {
                            title = type + name;
                        } else {
                            title = type;
                        }
                    }
                }
                return title;
            },
            validate: function(id) {
                let validationResult = false;
                if (id === -1) {
                    let toBeValidated = {
                        'valid-from': this.inner_campaign.parameters['valid-from'],
                        'valid-to': this.inner_campaign.parameters['valid-to'],
                    };

                    validationResult = utility.validate(toBeValidated);
                    validationResult = this.validateNumberProperties(this.inner_campaign, validationResult, "c_");
                } else {
                    let element = this.inner_campaign.elements[id];
                    let toBeValidated = {
                        'valid-from': element.valid_from,
                        'valid-to': element.valid_to,
                    };

                    validationResult = utility.validate(toBeValidated);
                    validationResult = this.validateNumberProperties(element, validationResult, "c_");
                }

                Vue.set(this.creator_errors, id+1, {
                    'name':  this.t_title(id),
                    'errors': validationResult.errors,
                });

                let valid = validationResult.valid;
                if(valid) {
                    for(let element of this.creator_errors) {
                        if(element && element.errors.length > 0) {
                            valid = false;
                            break;
                        }
                    }
                }

                this.$emit('validation', valid);
            },
            validateAll() {
                this.validate(-1);
                for (let id of this.inner_campaign.elements.keys()) {
                    this.validate(id);
                }
            },
            loadDefinition: function () {
                let type = '';
                if (this.is_parent_campaign) {
                    type = 'base';
                } else {
                    type = this.inner_properties.type;
                }
                this.loadDefinitionForType(type, "c_");
            },
            updateInnerProperties: function () {
                if (this.inner_campaign !== null) {
                    let new_properties = null;
                    if (this.is_parent_campaign) {
                        new_properties = this.inner_campaign;
                    } else {
                        new_properties = this.inner_campaign.elements[this.active_element];
                    }
                    if (new_properties) new_properties.index = this.active_element;
                    this.old_text_direction = null;
                    this.inner_properties = new_properties;

                    this.loadDefinition();
                }
            },
            updateTransitionForElements: function () {
                let transition = this.inner_properties.transition;
                let prev_transition = this.prev_inner_properties.transition;
              _.forEach(this.inner_properties.elements, function (element) {
                  if (element.transition === prev_transition) {
                      element.transition = transition;
                  }
              })
            },
            updateTransitionLengthForElements: function () {
                let transition_length = this.inner_properties.transition_length;
                let prev_transition_lenght = this.prev_inner_properties.transition_length;
                _.forEach(this.inner_properties.elements, function (element) {
                    if (element.transition_length === prev_transition_lenght) {
                        element.transition_length = transition_length;
                    }
                })
            },
            summedDuration: function () {
                let duration = 0;
                let defaultDuration = this.default_duration;

                let element_length = function(element) {
                    let el_length = 0;
                    if (element.type === 'ppt' || element.type === 'pdf') {
                        var page_to = parseInt((!isNaN(element.page_to) && element.page_to) ? element.page_to : element.page_count);
                        var page_from = parseInt((!isNaN(element.page_from) &&element.page_from) ? element.page_from : 1);
                        var interval = parseInt((!isNaN(element.interval) && element.interval) ? element.interval : 11);

                        if (interval && page_from && page_to) {
                            el_length = Math.max(interval * (page_to - page_from + 1), 0) ?? 0;
                        }
                    } else {
                        if (!(element.duration_original === 'unknown' || !element.duration_original)) {
                            el_length = element.duration_original;
                        }
                    }

                    if (element.duration_type === 2) {
                        el_length = el_length * parseInt(element.display_runs||"1") || 0;
                    } else if (element.duration_type === 1) {
                        el_length = defaultDuration;
                    } else {
                        el_length = parseInt(element.duration) || 0;
                    }

                    return el_length;
                }

                if (this.is_parent_campaign) {
                    _.forEach(this.inner_properties.elements, function (element) {
                        duration += element_length(element);
                    });
                } else {
                    duration += element_length(this.inner_properties);
                }

                if (duration > 60) {
                    let minutes = Math.floor(duration / 60),
                        seconds = duration % 60;

                    return minutes + this.t_m + ' ' + seconds + this.t_s;
                } else {
                    return duration + this.t_s;
                }
            },
            nameUpdated: function() {
                if(this.is_parent_campaign) {
                    EaseEventBus.fire('campaignNameUpdated', this.inner_properties.name);
                }
            },
            select_layer_items: function () {
                let items = this.all_layer_layers.filter((layer) => {
                    return this.is_selectable_layer(layer.value, 'campaign') === true;
                });
                items = items.map((layer) => {
                    layer.disabled = this.is_disabled_layer(layer, this.width, this.height, this.ignore_layer_size);
                    layer.value = layer.value.toString();
                    return layer;
                });
                return items;
            },
            onTitleClick() {
                if(!this.isTitleClickable) return
                this.settingsCollapsed = !this.settingsCollapsed
            }
        },
        mounted() {
            this.active_element = this.value;
            this.inner_campaign = _.extend({}, this.inner_campaign, this.data);
            this.transitions = Object.values(Laravel.transitions);

            this.loadDefinition();

            if(this.parent_layer_limitation) {
                this.parentLayerLimitation = JSON.parse(this.parent_layer_limitation);
            }
            if(this.parent_usergroup_limitation) {
                this.parentUsergroupLimitation = JSON.parse(this.parent_usergroup_limitation);
            }

            EaseEventBus.listen('removeSelectedElement', (id) => {
                if (this.creator_errors[id + 1] != null) {
                    this.creator_errors.splice(id + 1, 1);
                }
            });

            EaseEventBus.listen("unsavedChanges", (changes) => {
                this.unsavedChanges = changes;
            });

            EaseEventBus.listen('WORKAROUND_recalculate_settings_height', () => {
                this.timeoutHeightRecalculation();
            });

            EaseEventBus.listen('windowResize', (event) => {
                this.timeoutHeightRecalculation(true);
            });

            EaseEventBus.listen('collapseSettings', () => {
                this.settingsCollapsed = true;
            });
            EaseEventBus.listen('expandSettings', () => {
                this.settingsCollapsed = false;
            });

            this.heightStyle = this.settingsStyle();
        }
    }
</script>
